import { appStore, IEvent, MessageBus } from '@tom-ui/utils'
import { BerthResponse, CreateBerthRequest, UpdateBerthRequest } from '@yard/app/api'
import { EventTypes } from '@yard/messages/eventsTypes'
import { DeleteResponse } from '@yard/messages/models/delete-response.model'
import { berthService } from '@yard/services/berth.service'
import { computed, makeObservable } from 'mobx'
import { BaseEntityStore } from './base/BaseEntityStore'

export class BerthStore extends BaseEntityStore<BerthResponse> {
  private isFetched = false
  private readonly disposers: Array<() => void> = []

  constructor(private readonly messageBus: MessageBus) {
    super()
    makeObservable(this, {
      berths: computed,
    })

    this.disposers.push(
      this.messageBus.subscribeEvent<BerthResponse>(
        EventTypes.BerthUpsertedEvent,
        this.handleUpsertedEvent,
      ),
      this.messageBus.subscribeEvent<DeleteResponse>(
        EventTypes.BerthDeletedEvent,
        this.handleDeletedEvent,
      ),
    )
  }

  dispose(): void {
    this.disposers.forEach(d => d())
    this.disposers.length = 0
  }

  async loadBerths() {
    if (this.isFetched) return
    try {
      const berths = await berthService.list()
      this.updateStoreItems(berths)
      this.isFetched = true
    } catch (e) {
      appStore.setShowAlert('error', (e as any)?.response?.data?.detail ?? 'An error occurred')
    }
  }

  async addBerth(berth: CreateBerthRequest) {
    await berthService.create(berth)
  }

  async updateBerth(berth: UpdateBerthRequest) {
    await berthService.update(berth)
  }

  async deleteBerth(id: string) {
    await berthService.delete(id)
  }

  get berths(): Record<string, BerthResponse> {
    return this.items.reduce(
      (acc, berth) => {
        acc[berth.id] = berth
        return acc
      },
      {} as Record<string, BerthResponse>,
    )
  }

  private handleUpsertedEvent = (event: IEvent<BerthResponse>) => {
    const response = event.payload
    if (!response) return

    this.updateStoreItem(response, response.id)
  }

  private handleDeletedEvent = (event: IEvent<DeleteResponse>) => {
    const response = event.payload
    if (!response) return
    this.deleteStoreItem(response.id)
  }
}
